import { useCallback, useEffect, useRef, useState } from 'react'

import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop'

import { getCropImageUrl } from 'utils/images'
import { isGlobalId, toGlobalId } from 'utils/transformations/graphql'
import { isValidURL } from 'utils/validations'

import { useRenderToasters } from 'hooks/useRenderToasters'

import { Icon } from 'components/Core'
import Button from 'components/Core/ButtonV2/Button'
import AddImageModal from 'modals/Storefront/AddImageModal/AddImageModal.component'

import styles from './AddImageTooltip.module.css'
import {
  ASPECT_LANDSCAPE,
  ASPECT_PORTRAIT,
  ASPECT_SQUARE,
} from 'features/Storefront/constants'

export const getAspect = (aspect) => {
  switch (aspect) {
    case ASPECT_PORTRAIT:
      return 3 / 4
    case ASPECT_SQUARE:
      return 1
    case ASPECT_LANDSCAPE:
      return 3 / 2
    default:
      return 3 / 2
  }
}

export const useImage = ({
  value = {},
  openDynamicModal = () => {},
  onApplyChanges = () => {},
  aspect = '',
  onSelectImageAction = () => {},
  onCancelImageSelection = () => {},
}) => {
  const [selectedImage, setSelectedImage] = useState({})
  const [selectingImage, setSelectingImage] = useState(false)
  const [crop, setCrop] = useState()
  const [completedCrop, setCompletedCrop] = useState(null)
  const [croppedURL, setCroppedURL] = useState('')
  const [imageLink, setImageLink] = useState('')
  // The initial crop area covers the whole image, so only a crop the user
  // actually dragged should produce a cropped upload.
  const [isCroppedImage, setIsCroppedImage] = useState(false)
  // Set when the image cannot be fetched in CORS mode, so it is retried without
  // it — the preview must stay visible even when the canvas cannot be used.
  const [isCorsBlocked, setIsCorsBlocked] = useState(false)
  const imgRef = useRef(null)
  const previewCanvasRef = useRef(null)
  const cropAspect = getAspect(aspect)
  const { renderErrorToast } = useRenderToasters()

  useEffect(
    () => setImageLink(value?.imageExternalLink || ''),
    [value?.imageExternalLink],
  )

  // Largest centered crop area of the widget's aspect ratio that fits the image
  const seedCrop = useCallback(
    (img) => {
      const { width, height } = img
      if (!width || !height) {
        return
      }
      setCrop(
        centerCrop(
          makeAspectCrop({ unit: '%', width: 100 }, cropAspect, width, height),
          width,
          height,
        ),
      )
    },
    [cropAspect],
  )

  // Keep the crop area in sync when the widget's aspect ratio changes
  useEffect(() => {
    if (imgRef.current) {
      seedCrop(imgRef.current)
    }
  }, [seedCrop])

  const onImageLoad = useCallback(
    (event) => {
      imgRef.current = event.currentTarget
      seedCrop(event.currentTarget)
    },
    [seedCrop],
  )

  const onImageError = useCallback(() => {
    imgRef.current = null
    setIsCorsBlocked(true)
  }, [])

  const resetCrop = useCallback(() => {
    imgRef.current = null
    setCrop(undefined)
    setCompletedCrop(null)
    setIsCroppedImage(false)
    setCroppedURL('')
    setIsCorsBlocked(false)
  }, [])

  useEffect(() => {
    if (
      !completedCrop?.width ||
      !completedCrop?.height ||
      !previewCanvasRef.current ||
      !imgRef.current ||
      !isCroppedImage
    ) {
      return
    }

    const image = imgRef.current
    const canvas = previewCanvasRef.current

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    const ctx = canvas.getContext('2d')

    // Draw at the image's natural resolution so cropping does not downscale the
    // upload to the size of the (small) crop preview
    canvas.width = Math.floor(completedCrop.width * scaleX)
    canvas.height = Math.floor(completedCrop.height * scaleY)

    ctx.imageSmoothingQuality = 'high'

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height,
    )

    try {
      canvas.toBlob((croppedImage) => {
        setCroppedURL(croppedImage)
      }, 'image/png')
    } catch (error) {
      setIsCorsBlocked(true)
      setCroppedURL('')
      renderErrorToast({
        description: 'Cropping is unavailable for this image.',
      })
      console.error('Unable to generate the cropped image', error)
    }
  }, [completedCrop, isCroppedImage, renderErrorToast])

  const rawImageUrl = selectedImage?.url || value?.imageUrl

  useEffect(() => setIsCorsBlocked(false), [rawImageUrl])

  const cropSrc = getCropImageUrl(rawImageUrl)
  const isLocalSrc =
    !!cropSrc && (cropSrc.startsWith('data:') || cropSrc.startsWith('blob:'))
  const useCrossOrigin = !isLocalSrc && !isCorsBlocked

  const imageCrop = (
    <>
      <div className={styles.cropLabel}>
        <Icon name="crop" className={styles.cropIcon} />
        <span>Crop Image</span>
      </div>
      <div className={styles.cropContainer}>
        <ReactCrop
          aspect={cropAspect}
          className={styles.cropArea}
          crop={crop}
          keepSelection
          onChange={(_, percentCrop) => {
            setCrop(percentCrop)
            setIsCroppedImage(true)
          }}
          onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
        >
          <img
            alt="Crop preview"
            crossOrigin={useCrossOrigin ? 'anonymous' : undefined}
            key={`${cropSrc}-${useCrossOrigin ? 'cors' : 'plain'}`}
            onError={onImageError}
            onLoad={onImageLoad}
            src={cropSrc}
          />
        </ReactCrop>
      </div>
      {/* This canvas is not displayed but needed to get the croppedURL */}
      <canvas className={styles.previewCanvas} ref={previewCanvasRef} />
    </>
  )

  const imageSelection = (
    <Button
      className={styles.largeButton}
      onClick={() => {
        setSelectingImage(true)
        resetCrop()
        onSelectImageAction()
        openDynamicModal({
          component: AddImageModal,
          onCancel: () => {
            onCancelImageSelection()
            setSelectingImage(false)
          },
          onSelect: (value) => {
            const { id } = value
            const isGlobal = isGlobalId(id)
            const imageGlobalId = isGlobal ? id : toGlobalId('Upload', id)
            setSelectedImage({ ...value, id: imageGlobalId })
            setSelectingImage(false)
          },
          onExit: () => {},
        })
      }}
    >
      {value?.imageUrl ? 'Replace Image' : 'Select Image'}
    </Button>
  )

  const newUrl = croppedURL || selectedImage?.url
  const hasChanges =
    (newUrl && value?.imageUrl !== newUrl) ||
    (imageLink && value?.imageExternalLink !== imageLink)
  const parsedLink =
    imageLink.startsWith('http://') || imageLink.startsWith('https://')
      ? imageLink
      : `https://${imageLink}`
  const applyImageActions = async () => {
    const imageId = selectedImage.id || value?.imageId
    await onApplyChanges({
      imageId: imageId || value?.imageId,
      imageUrl: newUrl || value?.imageUrl,
      croppedImageSrc: croppedURL,
      imageExternalLink: isValidURL(parsedLink) ? parsedLink : '',
    })
  }
  return {
    imageSelection,
    imageLink,
    selectedImage,
    croppedURL,
    imageCrop,
    setImageLink,
    selectingImage,
    hasChanges,
    applyImageActions,
    setSelectedImage,
    resetCrop,
  }
}
